<template>
    <Spinner v-if="displaySpinner" />
    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-building">Business Entity</Eyebrow>
        </template>
        <template #title>
            <EditableTitle v-model="businessEntity" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="businessEntity?.identity"
                :entityId="businessEntity.identity"
                :entityType="EntityType.BUSINESS_ENTITY"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateBusinessEntityDescription"
            />
            <Button
                v-if="businessEntity?.identity && isEditing"
                v-tooltip.bottom="{
                    value: 'Convert this business entity to a domain term. Attributes and relationships will be dropped.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-book"
                label="Convert to term"
                :loading="isConverting"
                :disabled="isConverting"
                @click="confirmConvertToDomainTerm"
            />
        </template>
        <Section editable>
            <TextAppArtefact
                v-if="businessEntity?.identity && description"
                v-model="description"
                :entityId="businessEntity.identity"
                :entityType="EntityType.BUSINESS_ENTITY"
                :isEditing="isEditing"
                :stekzModel="businessEntity"
                ref="textAppArtefact"
            />
        </Section>

        <Section editable>
            <template #title>Metadata</template>
            <div class="metadata-grid">
                <div class="metadata-field">
                    <label class="metadata-label">Confidentiality</label>
                    <Dropdown
                        v-if="isEditing"
                        :modelValue="
                            (businessEntity as any)?.confidentiality_level ??
                            null
                        "
                        :options="confidentialityOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Not classified"
                        :showClear="true"
                        class="metadata-dropdown"
                        @update:modelValue="onConfidentialityChange"
                    />
                    <span v-else class="metadata-value">
                        {{
                            confidentialityOptions.find(
                                (o) =>
                                    o.value ===
                                    (businessEntity as any)
                                        ?.confidentiality_level
                            )?.label ?? "—"
                        }}
                    </span>
                </div>
            </div>
        </Section>

        <Section editable>
            <template #title>Attributes</template>
            <DataTable
                v-if="definition?.content"
                v-model:value="definitionRows"
                :editMode="isEditing ? 'cell' : undefined"
                @cell-edit-complete="onCellEditComplete"
                tableStyle="width: 100%"
            >
                <Column field="name" header="Name" style="width: 200px">
                    <template #editor="{ data, field }">
                        <InputText v-model="data[field]" class="w-full" />
                    </template>
                </Column>
                <Column field="type" header="Type" style="width: 180px">
                    <template #body="{ data }">
                        {{
                            attributeTypes.find((t) => t.value === data.type)
                                ?.label || data.type
                        }}
                    </template>
                    <template #editor="{ data, field }">
                        <Dropdown
                            v-model="data[field]"
                            :options="attributeTypes"
                            optionLabel="label"
                            optionValue="value"
                            class="w-full"
                            editable
                        />
                    </template>
                </Column>
                <Column field="description" header="Description">
                    <template #editor="{ data, field }">
                        <InputText v-model="data[field]" class="w-full" />
                    </template>
                </Column>
                <Column
                    v-if="isEditing"
                    :exportable="false"
                    style="width: 80px"
                    bodyStyle="text-align: right"
                >
                    <template #body="{ data }">
                        <Button
                            v-tooltip.top="'Remove attribute'"
                            icon="pi pi-times"
                            text
                            rounded
                            severity="danger"
                            @click="removeRow(data)"
                        />
                    </template>
                </Column>
            </DataTable>
            <Button
                v-if="isEditing"
                icon="pi pi-plus"
                label="Add Attribute"
                @click="addNewRow"
            />
        </Section>

        <!-- Relationships Section -->
        <Section v-if="outgoingRelationships.length > 0 || isEditing" editable>
            <template #title>Outgoing Relationships</template>
            <DataTable
                ref="outgoingRelationshipsTable"
                :value="outgoingRelationships"
                tableStyle="width: 100%"
            >
                <Column>
                    <template #body="{ data }">
                        <span class="relationship-sentence">
                            <span>
                                {{
                                    getEntityName(
                                        businessEntity?.identity || ""
                                    )
                                }}
                            </span>
                            <span class="relationship-type">{{
                                data.type
                            }}</span>
                            <RouterLink
                                :id="`outgoing-entity-link-${data.other_id}`"
                                class="entity-reference"
                                :to="{
                                    name: 'businessEntityPage',
                                    params: {
                                        orgName: $route.params.orgName,
                                        stekName: $route.params.stekName,
                                        businessEntityId: data.other_id,
                                    },
                                }"
                                >{{ getEntityName(data.other_id) }}</RouterLink
                            >
                        </span>
                    </template>
                </Column>
                <Column
                    v-if="isEditing"
                    :exportable="false"
                    style="width: 80px"
                    bodyStyle="text-align: right"
                >
                    <template #body="{ data }">
                        <Button
                            v-tooltip.top="'Remove relationship'"
                            icon="pi pi-times"
                            text
                            rounded
                            severity="danger"
                            @click="removeRelationship(data)"
                        />
                    </template>
                </Column>
            </DataTable>

            <!-- Add New Relationship Form - Only for Outgoing -->
            <div v-if="isEditing" class="add-relationship-form">
                <div class="form-row">
                    <div class="form-field">
                        <label for="relationshipType">Relationship</label>
                        <Dropdown
                            id="relationshipType"
                            v-model="newRelationship.type"
                            :options="relationshipTypes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select Relationship (or type in a custom one)"
                            class="dropdown-full-width"
                            editable
                        />
                    </div>
                    <div class="form-field">
                        <label for="targetEntity">Related Entity</label>
                        <Dropdown
                            id="targetEntity"
                            v-model="newRelationship.other_id"
                            :options="availableEntities"
                            optionLabel="name"
                            optionValue="identity"
                            placeholder="Select Entity"
                            class="dropdown-full-width"
                            filter
                            filterPlaceholder="Search entities"
                            :filterFields="['name']"
                            autoFilterFocus
                            resetFilterOnHide
                        />
                    </div>
                    <Button
                        icon="pi pi-plus"
                        label="Add Relationship"
                        @click="addRelationship"
                        :disabled="!canAddRelationship"
                    />
                </div>
            </div>
        </Section>

        <Section>
            <EntityTaskRefs v-if="businessEntity?.identity" />
        </Section>

        <!-- Incoming Relationships Section -->
        <Section v-if="incomingRelationships.length > 0 || isEditing" editable>
            <template #title>Incoming Relationships</template>
            <DataTable
                ref="incomingRelationshipsTable"
                :value="incomingRelationships"
                tableStyle="width: 100%"
            >
                <Column>
                    <template #body="{ data }">
                        <span class="relationship-sentence">
                            <RouterLink
                                :id="`incoming-entity-link-${data.other_id}`"
                                class="entity-reference"
                                :to="{
                                    name: 'businessEntityPage',
                                    params: {
                                        orgName: $route.params.orgName,
                                        stekName: $route.params.stekName,
                                        businessEntityId: data.other_id,
                                    },
                                }"
                                >{{ getEntityName(data.other_id) }}</RouterLink
                            >
                            <span class="relationship-type">{{
                                data.type
                            }}</span>
                            <span>{{
                                getEntityName(businessEntity?.identity || "")
                            }}</span>
                        </span>
                    </template>
                </Column>
                <Column
                    v-if="isEditing"
                    :exportable="false"
                    style="width: 80px"
                    bodyStyle="text-align: right"
                >
                    <template #body="{ data }">
                        <Button
                            v-tooltip.top="'Remove relationship'"
                            icon="pi pi-times"
                            text
                            rounded
                            severity="danger"
                            @click="removeIncomingRelationship(data)"
                        />
                    </template>
                </Column>
            </DataTable>

            <!-- Add New Incoming Relationship Form -->
            <div v-if="isEditing" class="add-relationship-form">
                <div class="form-row">
                    <div class="form-field">
                        <label for="incomingSourceEntity">Source Entity</label>
                        <Dropdown
                            id="incomingSourceEntity"
                            v-model="newIncomingRelationship.other_id"
                            :options="availableIncomingEntities"
                            optionLabel="name"
                            optionValue="identity"
                            placeholder="Select Entity"
                            class="dropdown-full-width"
                            filter
                            filterPlaceholder="Search entities"
                            :filterFields="['name']"
                            autoFilterFocus
                            resetFilterOnHide
                        />
                    </div>
                    <div class="form-field">
                        <label for="incomingRelationshipType"
                            >Relationship</label
                        >
                        <Dropdown
                            id="incomingRelationshipType"
                            v-model="newIncomingRelationship.type"
                            :options="relationshipTypes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select Relationship (or type in a custom one)"
                            class="dropdown-full-width"
                            editable
                        />
                    </div>
                    <Button
                        icon="pi pi-plus"
                        label="Add Relationship"
                        @click="addIncomingRelationship"
                        :disabled="!canAddIncomingRelationship"
                    />
                </div>
            </div>
        </Section>
    </Page>
    <!-- Outgoing Relationships Popups -->
    <template
        v-for="relationship in outgoingRelationships"
        :key="`outgoing-${relationship.other_id}`"
    >
        <Popup
            v-if="!isEditing && relationship.other_id"
            :anchor="`a#outgoing-entity-link-${relationship.other_id}`"
        >
            <GenericCard
                :entityId="relationship.other_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>

    <!-- Incoming Relationships Popups -->
    <template
        v-for="relationship in incomingRelationships"
        :key="`incoming-${relationship.other_id}`"
    >
        <Popup
            v-if="!isEditing && relationship.other_id"
            :anchor="`a#incoming-entity-link-${relationship.other_id}`"
        >
            <GenericCard
                :entityId="relationship.other_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable, { DataTableCellEditCompleteEvent } from "primevue/datatable";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";

import EntityTaskRefs from "@/components/BusinessEntity/EntityTaskRefs.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Popup from "@/components/Popup.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { api } from "@/lib/openapi";
import { RelationshipDirection } from "@/lib/openapi/gen";
import Spinner from "@/shared/Spinner.vue";
import {
    BusinessEntity,
    useBusinessEntitiesStore,
} from "@/stores/business-entities-store";
import {
    BusinessEntityRelationship,
    useBusinessEntityManager,
    useBusinessEntityStore,
} from "@/stores/business-entity-store";
import { EntityType } from "@/stores/entity-store";

const { entities } = storeToRefs(useBusinessEntitiesStore());

const { isEditing } = useEditManager();
const { businessEntity, isFetching, description, definition } =
    useBusinessEntityManager();

const {
    addRelationship: addRelationshipToStore,
    removeRelationship: removeRelationshipFromStore,
    markMetadataModified,
} = useBusinessEntityStore();
const { generateContent } = useGenerateDescriptionDialog();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();
const route = useRoute();

const displaySpinner = ref(false);
const outgoingRelationshipsTable = ref();
const incomingRelationshipsTable = ref();
const isConverting = ref(false);

interface DefinitionRow {
    name: string;
    type: string;
    description: string;
}

interface DefinitionField {
    type: string;
    description: string;
}

interface DefinitionContent {
    [key: string]: DefinitionField;
}

const confidentialityOptions = [
    { label: "Public", value: "public" },
    { label: "Internal", value: "internal" },
    { label: "Confidential", value: "confidential" },
    { label: "Restricted", value: "restricted" },
];

const attributeTypes = [
    { label: "Textual", value: "textual" },
    { label: "Numeric", value: "numeric" },
    { label: "Monetary", value: "monetary" },
    { label: "Percentage", value: "percentage" },
    { label: "Date", value: "date" },
    { label: "Date & Time", value: "datetime" },
    { label: "Duration", value: "duration" },
    { label: "Boolean", value: "boolean" },
    { label: "List", value: "list" },
    { label: "Code", value: "code" },
    { label: "Identifier", value: "identifier" },
];

const onConfidentialityChange = (value: string | null) => {
    if (!businessEntity.value) return;
    businessEntity.value.confidentiality_level = value ?? null;
    markMetadataModified();
};

const definitionRows = computed<DefinitionRow[]>({
    get: () => {
        if (!definition.value?.content) return [];
        try {
            const content: DefinitionContent = JSON.parse(
                definition.value.content
            );
            return Object.entries(content).map(([name, field]) => ({
                name,
                type: field.type,
                description: field.description,
            }));
        } catch {
            return [];
        }
    },
    set: (newValue: DefinitionRow[]) => {
        if (definition.value) {
            const content: DefinitionContent = {};
            newValue.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    },
});

const onCellEditComplete = (event: DataTableCellEditCompleteEvent) => {
    let { data, newValue, field } = event;
    if (typeof field === "string" && field in data) {
        data[field as keyof DefinitionRow] = newValue;
        if (definition.value) {
            const content: DefinitionContent = {};
            definitionRows.value.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    }
};

watch(
    isFetching,
    (newValue) => {
        displaySpinner.value = newValue;
    },
    { immediate: true }
);

const generateBusinessEntityDescription = async () => {
    if (!businessEntity.value) {
        return;
    }
    await generateContent(
        businessEntity.value.identity,
        EntityType.BUSINESS_ENTITY,
        description.value?.updateContent.bind(description.value)
    );
};

const addNewRow = () => {
    const newRow: DefinitionRow = {
        name: `attribute_${definitionRows.value.length + 1}`,
        type: "textual",
        description: "",
    };

    if (definition.value) {
        const content: DefinitionContent = {};
        [...definitionRows.value, newRow].forEach((row) => {
            content[row.name] = {
                type: row.type,
                description: row.description,
            };
        });
        definition.value.content = JSON.stringify(content);
    }
};

const removeRow = (rowData: DefinitionRow) => {
    if (definition.value) {
        const content: DefinitionContent = {};
        definitionRows.value
            .filter((row) => row.name !== rowData.name)
            .forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
        definition.value.content = JSON.stringify(content);
    }
};

const relationships = computed(() => {
    return businessEntity.value?.relationships || [];
});

const outgoingRelationships = computed(() => {
    if (!businessEntity.value?.identity) return [];
    return relationships.value.filter(
        (rel) => rel.direction === RelationshipDirection.OUTGOING
    );
});

const incomingRelationships = computed(() => {
    if (!businessEntity.value?.identity) return [];
    return relationships.value.filter(
        (rel) => rel.direction === RelationshipDirection.INCOMING
    );
});

const newRelationship = ref({
    other_id: null as string | null,
    type: "",
});

const newIncomingRelationship = ref({
    other_id: null as string | null,
    type: "",
});

// Predefined relationship types
const relationshipTypes = [
    { label: "belongs to", value: "belongs to" },
    { label: "uses", value: "uses" },
    { label: "contains", value: "contains" },
    { label: "has", value: "has" },
    { label: "extends", value: "extends" },
    { label: "implements", value: "implements" },
    { label: "depends on", value: "depends on" },
    { label: "composed of", value: "composed of" },
];

// Computed properties
const canAddRelationship = computed(() => {
    return (
        newRelationship.value.other_id &&
        newRelationship.value.type &&
        newRelationship.value.other_id !== businessEntity.value?.identity
    );
});

const canAddIncomingRelationship = computed(() => {
    return (
        newIncomingRelationship.value.other_id &&
        newIncomingRelationship.value.type &&
        newIncomingRelationship.value.other_id !==
            businessEntity.value?.identity
    );
});

const sortEntitiesByName = (list: BusinessEntity[]) =>
    list
        .slice()
        .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );

const availableEntities = computed(() => {
    if (!businessEntity.value?.identity) return;

    return sortEntitiesByName(
        entities.value.filter(
            (entity: BusinessEntity) =>
                entity.identity !== businessEntity.value?.identity &&
                !outgoingRelationships.value.some(
                    (r) => r.other_id === entity.identity
                )
        )
    );
});

const availableIncomingEntities = computed(() => {
    if (!businessEntity.value?.identity) return;

    return sortEntitiesByName(
        entities.value.filter(
            (entity: BusinessEntity) =>
                entity.identity !== businessEntity.value?.identity &&
                !incomingRelationships.value.some(
                    (r) => r.other_id === entity.identity
                )
        )
    );
});

const addRelationship = async () => {
    if (!canAddRelationship.value || !businessEntity.value?.identity) return;

    const newRel: BusinessEntityRelationship = {
        other_id: newRelationship.value.other_id!,
        type: newRelationship.value.type,
        direction: RelationshipDirection.OUTGOING,
    };

    addRelationshipToStore(newRel);

    // Reset the form after adding the relationship
    newRelationship.value = {
        other_id: null,
        type: "",
    };
};

const addIncomingRelationship = async () => {
    if (!canAddIncomingRelationship.value || !businessEntity.value?.identity) {
        return;
    }

    const newRel: BusinessEntityRelationship = {
        other_id: newIncomingRelationship.value.other_id!,
        type: newIncomingRelationship.value.type,
        direction: RelationshipDirection.INCOMING,
    };

    addRelationshipToStore(newRel);

    newIncomingRelationship.value = {
        other_id: null,
        type: "",
    };
};

const removeRelationship = async (relationship: BusinessEntityRelationship) => {
    // Remove from the list
    removeRelationshipFromStore(relationship.other_id, relationship.direction);
};

const removeIncomingRelationship = async (
    relationship: BusinessEntityRelationship
) => {
    removeRelationshipFromStore(relationship.other_id, relationship.direction);
};

const getEntityName = (entityId: string) => {
    const entity = entities.value.find((e) => e.identity === entityId);
    return entity?.name || entityId;
};

const confirmConvertToDomainTerm = (event: Event) => {
    if (!businessEntity.value?.identity) return;
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message:
            "Convert this business entity to a domain term? Attributes, " +
            "relationships and metadata will be dropped. This is not reversible.",
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: "Cancel", severity: "secondary", outlined: true },
        acceptProps: { label: "Convert" },
        accept: convertToDomainTerm,
    });
};

const convertToDomainTerm = async () => {
    if (!businessEntity.value?.identity || isConverting.value) return;
    isConverting.value = true;
    try {
        const { response, data } =
            await api.startDomainBusinessEntityMgtConvertToDomainTermStartPost({
                body: { business_entity_id: businessEntity.value.identity },
            });
        if (!response.ok || !data) {
            toast.add({
                severity: "error",
                summary: `Error ${response.status}`,
                detail:
                    (data as { detail?: { msg?: string }[] })?.detail?.[0]
                        ?.msg ?? "Could not convert business entity.",
                life: 5000,
            });
            return;
        }
        await router.replace({
            name: "domainTermPage",
            params: {
                orgName: route.params.orgName,
                stekName: route.params.stekName,
                domainTermId: data.identity,
            },
        });
    } finally {
        isConverting.value = false;
    }
};
</script>

<style scoped lang="scss">
.metadata-grid {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    margin: 0.5rem 0;
}

.metadata-field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 180px;
}

.metadata-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.metadata-value {
    font-size: 0.95rem;
}

.metadata-dropdown {
    min-width: 180px;
}

.entity-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.entity-link {
    text-decoration: none;
}

.relationship-sentence {
    white-space: pre-wrap;
}

.entity-reference {
    color: var(--stekz-primary-color) !important;
    font-weight: bold;
    text-decoration: none;
}

.entity-reference:hover {
    text-decoration: underline;
}

.relationship-type {
    padding: 0 0.5rem;

    font-style: italic;
}

.add-relationship-form {
    margin-top: 1rem;
}

.form-row {
    display: flex;
    gap: 0.5rem;
    align-items: end;
    flex-wrap: wrap;
}

.form-field {
    display: flex;
    flex-direction: column;
    min-width: 200px;
    flex: 1;
}

.form-field label {
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.dropdown-full-width {
    width: 100%;
}
</style>
